import type {
  CSSProperties,
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  Ref,
} from 'react';
import { IcnCheck, IcnPlus } from '../../icons/index.js';
import { useI18nT } from '../../i18n/index.js';
import { MOBILE_FLOATING_PANEL_STYLE } from '../shared/mobileFloating.js';
import type { SelectTagRow } from './types.js';

export interface SelectTagPanelProps<V extends string = string> {
  panelId: string;
  triggerId: string;
  rows: readonly SelectTagRow<V>[];
  highlightIndex: number;
  query: string;
  searchable: boolean;
  searchPlaceholder: string;
  floatingStyle: CSSProperties;
  maxHeight: number;
  placement: 'top' | 'bottom';
  mobileSheet: boolean;
  renderEmpty: ((query: string) => ReactNode) | undefined;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onQueryChange: (value: string) => void;
  onOptionHover: (index: number) => void;
  onOptionClick: (row: SelectTagRow<V>) => void;
  panelRef: Ref<HTMLDivElement>;
  searchInputRef: Ref<HTMLInputElement>;
}

export function SelectTagPanel<V extends string = string>(
  props: SelectTagPanelProps<V>,
): ReactNode {
  const t = useI18nT();
  const {
    panelId,
    triggerId,
    rows,
    highlightIndex,
    query,
    searchable,
    searchPlaceholder,
    floatingStyle,
    maxHeight,
    placement,
    mobileSheet,
    renderEmpty,
    onKeyDown,
    onQueryChange,
    onOptionHover,
    onOptionClick,
    panelRef,
    searchInputRef,
  } = props;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onQueryChange(event.target.value);
  };

  return (
    <div
      ref={panelRef}
      id={panelId}
      className="vq-select-tag__panel"
      data-component="SelectTagPanel"
      data-placement={placement}
      data-mobile-sheet={mobileSheet ? 'true' : undefined}
      data-searchable={searchable ? 'true' : undefined}
      style={
        mobileSheet
          ? MOBILE_FLOATING_PANEL_STYLE
          : {
              ...floatingStyle,
              maxHeight: `${String(maxHeight)}px`,
            }
      }
      onKeyDown={onKeyDown}
    >
      {searchable ? (
        <div className="vq-select-tag__search" data-slot="search">
          <input
            ref={searchInputRef}
            type="text"
            role="searchbox"
            className="vq-select-tag__search-input"
            placeholder={searchPlaceholder}
            value={query}
            onChange={handleSearchChange}
            aria-controls={panelId}
            aria-autocomplete="list"
          />
        </div>
      ) : null}
      <ul
        role="listbox"
        aria-multiselectable="true"
        aria-labelledby={triggerId}
        className="vq-select-tag__options"
        data-slot="options"
      >
        {rows.map((row, index) => (
          <li
            key={row.key}
            id={`${panelId}-opt-${String(index)}`}
            role="option"
            aria-selected={row.selected}
            aria-disabled={row.disabled || undefined}
            className="vq-select-tag__option"
            data-highlighted={index === highlightIndex ? 'true' : undefined}
            data-selected={row.selected ? 'true' : undefined}
            data-disabled={row.disabled ? 'true' : undefined}
            data-custom={row.custom ? 'true' : undefined}
            onMouseEnter={() => {
              if (!row.disabled) onOptionHover(index);
            }}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              onOptionClick(row);
            }}
          >
            <span className="vq-select-tag__option-check" aria-hidden="true">
              {row.selected ? <IcnCheck size={12} /> : row.custom ? <IcnPlus size={12} /> : null}
            </span>
            <span className="vq-select-tag__option-label">
              {row.option.icon !== undefined ? (
                <span className="vq-select-tag__option-icon" aria-hidden="true">
                  {row.option.icon}
                </span>
              ) : null}
              <span className="vq-select-tag__option-text">{row.option.label}</span>
              {row.option.description !== undefined ? (
                <span className="vq-select-tag__option-desc">{row.option.description}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {rows.length === 0 ? (
        <div className="vq-select-tag__empty" role="status">
          {renderEmpty !== undefined
            ? renderEmpty(query)
            : query.length > 0
              ? t('select.noMatch', { query })
              : t('select.empty')}
        </div>
      ) : null}
    </div>
  );
}
